import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {boolean} */
  #didRunInitialCheck = false;
  /** @type {Promise<any> | null} */
  static #productJsonPromise = null;

  connectedCallback() {
    // Bind variant events so we can disable/enable and set value appropriately
    super.connectedCallback?.();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const scope = this.closest('.shopify-section, dialog, product-card');
    scope?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Perform an initial availability check on mount so qty is correct on first paint
    // Especially important when landing on a sold-out variant via ?variant=...
    this.runInitialAvailabilityCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController?.abort();
  }
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Temporarily disable quantity controls while variant availability resolves
   */
  #onVariantSelected = () => {
    this.#setDisabledState(true, { forceZero: false });
  };

  /**
   * Lock quantity at 0 when unavailable; restore to min when available
   * @param {import('./events').VariantUpdateEvent | Event} event
   */
  #onVariantUpdate = (event) => {
    // @ts-ignore - detail typing at runtime
    const available = !!(event?.detail?.resource && event.detail.resource.available);
    this.#setDisabledState(!available, { forceZero: !available });
  };

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Enables/disables input and buttons; optionally forces value to 0
   * @param {boolean} disabled
   * @param {{ forceZero?: boolean }} options
   */
  #setDisabledState(disabled, { forceZero = false } = {}) {
    const input = this.refs.quantityInput;
    const minus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[name="minus"]'));
    const plus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[name="plus"]'));

    if (disabled) {
      if (forceZero) input.value = '0';
      input.setAttribute('disabled', '');
      input.setAttribute('aria-disabled', 'true');
      minus?.setAttribute('disabled', '');
      minus?.setAttribute('aria-disabled', 'true');
      plus?.setAttribute('disabled', '');
      plus?.setAttribute('aria-disabled', 'true');
      this.classList.add('is-disabled');
      return;
    }

    input.removeAttribute('disabled');
    input.removeAttribute('aria-disabled');
    minus?.removeAttribute('disabled');
    minus?.removeAttribute('aria-disabled');
    plus?.removeAttribute('disabled');
    plus?.removeAttribute('aria-disabled');
    this.classList.remove('is-disabled');

    const min = parseInt(input.getAttribute('min') || '1', 10);
    const current = parseInt(input.value || '0', 10);
    if (!Number.isFinite(current) || current < min) {
      input.value = String(min);
    }
  }

  /**
   * Runs an initial availability check based on the current PDP state.
   * - If landing on a sold-out variant via URL (?variant=ID), disable qty immediately
   * - If the whole product is sold out, disable qty
   */
  runInitialAvailabilityCheck() {
    if (this.#didRunInitialCheck) return;
    this.#didRunInitialCheck = true;

    // Only attempt PDP JSON fetch for product pages
    const path = window.location?.pathname || '';
    if (!/\/products\//.test(path)) return;

    // Get the current variant ID from the closest product form or URL param
    const form = this.closest('form');
    const variantInput = form?.querySelector('input[name="id"]');
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const currentVariantId = (urlVariantId || variantInput?.value || '').trim();

    // Fetch and cache the product JSON once per page
    if (!QuantitySelectorComponent.#productJsonPromise) {
      const productJsonUrl = path.replace(/\/$/, '') + '.js';
      QuantitySelectorComponent.#productJsonPromise = fetch(productJsonUrl)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to fetch product JSON'))))
        .catch(() => null);
    }

    QuantitySelectorComponent.#productJsonPromise.then((productJson) => {
      if (!productJson || !Array.isArray(productJson.variants)) return;

      // If no variants are available, disable
      const anyAvailable = productJson.variants.some((v) => !!v.available);
      if (!anyAvailable) {
        this.#setDisabledState(true, { forceZero: true });
        return;
      }

      // If a specific variant is active (via URL or input), respect its availability
      if (currentVariantId) {
        const variant = productJson.variants.find((v) => String(v.id) === String(currentVariantId));
        if (variant && variant.available === false) {
          this.#setDisabledState(true, { forceZero: true });
        }
      }
    });
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
